<script setup lang="ts">
import { watch, onUnmounted, computed, ref } from 'vue'
import Button from 'primevue/button'
import { useNetworkEdit } from '@/composables/useNetworkEdit'
import { useNetworkStore } from '@/stores/network'
import {
  moveNetworkSegmentLayerForOsmMode,
  refreshNetworkSegments,
  removeOsmOverlayLayer,
  setNetworkSegmentsOsmMode,
  setOsmOverlayFeatures,
  updateOsmSelectedFeatures,
  setOsmRemoveOverlayFeatures,
  setOsmLeafOverlayFeatures,
  setOsmViewportChangeListener,
  clearNetworkSegmentClickHandler
} from '@/services/map.service'
import { apiClient } from '@/services/api.service'
import { ENDPOINTS } from '@/endpoints'
import { showErrorToast, showSuccessToast } from '@/composables/toast'

const networkStore = useNetworkStore()
const edit = useNetworkEdit()

const isSaving = edit.isSaving
const addCount = edit.addCount
const removeCount = edit.removeCount
const hasSelectedItems = edit.canSave
const limitReached = computed(() => edit.atAddLimit.value || edit.atRemoveLimit.value)
const showLoadingMessage = computed(() => edit.isLoadingWays.value && !edit.hasLoadedWays.value)

const zoomTooLow = computed(() => {
  const zoom = edit.currentZoom.value
  return zoom != null && zoom < edit.MIN_ZOOM_FOR_OSM_SEGMENTS
})
const selectedNetwork = computed(() => {
  const nid = edit.selectedNetworkId.value
  if (nid == null) return null
  return networkStore.networks?.find((n) => n.id === nid) ?? null
})
const selectedNetworkName = computed(() => selectedNetwork.value?.name ?? '')
const selectedNetworkStrokeColor = computed(() => selectedNetwork.value?.color?.[1]?.hexValue)
const networkFeatures = computed(() => selectedNetwork.value?.geoData?.features ?? [])

const leafSegmentGids = ref<number[] | null>(null)
const isLeafLoading = ref(false)
const leafCount = computed(() => leafSegmentGids.value?.length ?? 0)

const loadErrorMessage = computed(() => {
  const error = edit.loadError.value
  if (!error) return ''
  const normalized = String(error).trim()
  if (normalized === '' || normalized.toLowerCase() === 'null') return ''
  return normalized
})

async function exitEditMode() {
  const nid = edit.selectedNetworkId.value
  leafSegmentGids.value = null
  setOsmViewportChangeListener(null)
  if (nid != null) {
    moveNetworkSegmentLayerForOsmMode(nid, false)
    setNetworkSegmentsOsmMode(nid, false, selectedNetworkStrokeColor.value)
  }
  clearNetworkSegmentClickHandler()
  await removeOsmOverlayLayer()
  edit.exitMode()
}

async function loadLeafSegments() {
  const nid = edit.selectedNetworkId.value
  if (nid == null) return
  isLeafLoading.value = true
  try {
    const res = await apiClient.get<{ leafGids: number[] }>(ENDPOINTS.networkLeafSegments(nid))
    leafSegmentGids.value = res.data.leafGids ?? []
    setOsmLeafOverlayFeatures(networkFeatures.value, new Set(leafSegmentGids.value))
  } finally {
    isLeafLoading.value = false
  }
}

watch(
  () => edit.osmWaysFeatures.value,
  (features) => {
    if (edit.isEditMode.value) {
      setOsmOverlayFeatures(features, edit.selectedWayIds.value)
    }
  }
)

watch(
  () => edit.selectedWayIds.value,
  (selectedIds) => {
    if (edit.isEditMode.value) {
      updateOsmSelectedFeatures(selectedIds)
    }
  }
)

watch(
  [() => edit.selectedSegmentGids.value, networkFeatures],
  ([selectedGids, features]) => {
    if (edit.isEditMode.value) {
      setOsmRemoveOverlayFeatures(features, selectedGids)
    }
  }
  // Note: deep: true removed - Sets are replaced (not mutated) so shallow watch is sufficient
)

watch(
  [() => leafSegmentGids.value, networkFeatures],
  ([gids, features]) => {
    if (edit.isEditMode.value && gids != null) {
      setOsmLeafOverlayFeatures(features, new Set(gids))
    }
  }
  // Note: deep: true removed - arrays are replaced (not mutated) so shallow watch is sufficient
)

watch(
  () => edit.isEditMode.value,
  async (isMode, wasMode) => {
    if (!isMode && wasMode) {
      setOsmViewportChangeListener(null)
      await removeOsmOverlayLayer()
    }
  }
)

async function onSave() {
  if (!edit.canSave.value) return
  const result = await edit.save()
  if (result == null) {
    if (edit.saveError.value) showErrorToast(edit.saveError.value)
    return
  }
  const nid = edit.selectedNetworkId.value
  await exitEditMode()
  const parts: string[] = []
  if (result.added > 0) parts.push(`${result.added} toegevoegd`)
  if (result.removed > 0) parts.push(`${result.removed} verwijderd`)
  if (parts.length > 0) showSuccessToast(parts.join(', ') + '.')
  if (nid != null && (result.added > 0 || result.removed > 0)) {
    await networkStore.networkUpdated(nid)
    await refreshNetworkSegments(nid)
  }
}

onUnmounted(async () => {
  if (edit.isEditMode.value) {
    const nid = edit.selectedNetworkId.value
    if (nid != null) {
      moveNetworkSegmentLayerForOsmMode(nid, false)
      setNetworkSegmentsOsmMode(nid, false, selectedNetworkStrokeColor.value)
    }
    clearNetworkSegmentClickHandler()
    await removeOsmOverlayLayer()
  }
})
</script>

<template>
  <div
    v-if="edit.isEditMode"
    class="flex flex-column gap-2 p-3 bg-white border-round-xl shadow-container"
  >
    <div class="flex align-items-center justify-content-between gap-3 flex-wrap">
      <div class="flex flex-column gap-1">
        <span class="font-semibold">Netwerk bewerken</span>
        <span class="text-sm text-color-secondary">
          <strong>{{ selectedNetworkName }}</strong>
        </span>
      </div>
      <div class="flex align-items-center gap-2">
        <Button
          class="border-round-xl"
          severity="secondary"
          type="button"
          size="small"
          label="Annuleren"
          :disabled="isSaving"
          @click="exitEditMode"
        />
        <Button
          class="border-round-xl"
          type="button"
          size="small"
          :disabled="!edit.canSave || isSaving"
          :loading="isSaving"
          label="Opslaan"
          @click="onSave"
        />
      </div>
    </div>
    <div class="flex align-items-center gap-2 flex-wrap border-top-1 surface-border pt-2 mt-2">
      <Button
        type="button"
        size="small"
        class="border-round-xl"
        severity="secondary"
        :label="leafSegmentGids === null ? 'Toon leaf segmenten' : 'Vernieuw leaf segmenten'"
        :loading="isLeafLoading"
        :disabled="isLeafLoading"
        @click="loadLeafSegments"
      />
      <span v-if="leafSegmentGids !== null" class="text-sm text-yellow-600 font-medium">
        {{ leafCount }} leaf segment{{ leafCount === 1 ? '' : 'en' }} (niet aan beide uiteinden
        verbonden)
      </span>
    </div>
    <div
      v-if="hasSelectedItems"
      class="flex flex-column gap-1 border-top-1 surface-border pt-2 mt-2"
    >
      <div class="text-sm font-semibold text-color">Overzicht wijzigingen</div>
      <div class="flex align-items-center gap-2 flex-wrap">
        <span v-if="addCount > 0" class="text-sm text-green-600 font-medium">
          {{ addCount }} toe te voegen
        </span>
        <span v-if="addCount > 0 && removeCount > 0" class="text-sm text-color-secondary"> • </span>
        <span v-if="removeCount > 0" class="text-sm text-red-600 font-medium">
          {{ removeCount }} te verwijderen
        </span>
      </div>
    </div>
    <div v-if="zoomTooLow" class="text-sm text-orange-600">
      Zoom verder in om wegen te laden (min. zoom {{ edit.MIN_ZOOM_FOR_OSM_SEGMENTS }}).
    </div>
    <div v-else-if="loadErrorMessage" class="text-sm text-red-600">
      {{ loadErrorMessage }}
    </div>
    <div v-else-if="showLoadingMessage" class="text-sm text-color-secondary">Wegen laden…</div>
    <div v-else class="text-sm text-color-secondary">
      Klik op een bestaand segment (rood) om te verwijderen, of op een oranje weg om toe te voegen.
      Max. {{ edit.MAX_SELECTION }} per actie.
      <span v-if="limitReached" class="text-orange-600"> Limiet bereikt.</span>
    </div>
    <div class="edit-legend border-top-1 surface-border pt-2 mt-1">
      <div class="edit-legend__row">
        <span class="edit-legend__line edit-legend__line--network" aria-hidden="true" />
        <span class="text-xs text-color-secondary">Bestaand netwerk</span>
      </div>
      <div class="edit-legend__row">
        <span class="edit-legend__line edit-legend__line--orange" aria-hidden="true" />
        <span class="text-xs text-color-secondary">Te selecteren om toe te voegen</span>
      </div>
      <div class="edit-legend__row">
        <span class="edit-legend__line edit-legend__line--green" aria-hidden="true" />
        <span class="text-xs text-color-secondary">Toe te voegen</span>
      </div>
      <div class="edit-legend__row">
        <span class="edit-legend__line edit-legend__line--red" aria-hidden="true" />
        <span class="text-xs text-color-secondary">Te verwijderen</span>
      </div>
      <div class="edit-legend__row">
        <span class="edit-legend__line edit-legend__line--leaf" aria-hidden="true" />
        <span class="text-xs text-color-secondary">Leaf (niet aan beide uiteinden verbonden)</span>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.shadow-container {
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.4);
}

.edit-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem 1.25rem;
  align-items: center;
}

.edit-legend__row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.edit-legend__line {
  display: inline-block;
  width: 1.5rem;
  height: 0;
  border-bottom-width: 3px;
  border-bottom-style: solid;
}

.edit-legend__line--network {
  border-bottom-color: #374151;
}

.edit-legend__line--orange {
  border-bottom-color: #f97316;
}

.edit-legend__line--green {
  border-bottom-color: #22c55e;
}

.edit-legend__line--red {
  border-bottom-color: #dc2626;
}

.edit-legend__line--leaf {
  border-bottom-color: #facc15;
}
</style>
